import React, { useEffect, useRef } from "react";
import type { Polygon } from "geojson";
import OpenLayersMap from "ol/Map";
import View from "ol/View";
import TileLayer from "ol/layer/Tile";
import XYZ from "ol/source/XYZ";
import { TileJSON } from "ol/source";
import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import GeoJSON from "ol/format/GeoJSON";
import Feature from "ol/Feature";
import { Geometry } from "ol/geom";
import { Style, Stroke, Fill, Text } from "ol/style";
import { isEmpty as isEmptyExtent } from "ol/extent";

export type TPreviewCandidate = {
  id: string;
  geometry: Polygon;
  name: string;
  selected: boolean;
  /** Cannot be created (too large): drawn, never selectable. */
  disabled: boolean;
};

interface IProps {
  candidates: TPreviewCandidate[];
  /** The season's existing boundaries (map projection), drawn for context. */
  existing: Feature<Geometry>[];
  onToggle: (id: string) => void;
  height?: number;
}

// Same imagery the Fields map uses: low-res tiles when zoomed out, the
// high-res template from z12 — judging a boundary needs the ground visible.
const lowResUrl = process.env.REACT_APP_LOW_RES_SATELITE_API_URL!;
const highResUrl = process.env.REACT_APP_HIGH_RES_SATELITE_API_URL_TEMPLATE!;

const label = (text: string) =>
  new Text({
    text,
    font: "600 12px Nunito Sans, sans-serif",
    fill: new Fill({ color: "#ffffff" }),
    stroke: new Stroke({ color: "rgba(0,0,0,0.75)", width: 3 }),
    overflow: false,
  });

const candidateStyle = (f: Feature<Geometry>) => {
  const selected = Boolean(f.get("selected")) && !f.get("disabled");
  return new Style({
    stroke: selected
      ? new Stroke({ color: "#68ad18", width: 3 })
      : new Stroke({ color: "#ffffff", width: 2, lineDash: [6, 5] }),
    fill: new Fill({
      color: selected ? "rgba(104, 173, 24, 0.35)" : "rgba(255, 255, 255, 0.12)",
    }),
    text: label(String(f.get("name") ?? "")),
  });
};

const existingStyle = new Style({
  stroke: new Stroke({ color: "rgba(255, 255, 255, 0.85)", width: 1.25 }),
  fill: new Fill({ color: "rgba(0, 0, 0, 0.12)" }),
});

/**
 * Satellite preview of the candidate boundaries. Clicking a candidate toggles
 * it — the same as its checkbox in the list — so the choice can be made where
 * the shape is visible.
 */
const FromTracksPreviewMap: React.FC<IProps> = ({
  candidates,
  existing,
  onToggle,
  height = 320,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<OpenLayersMap | null>(null);
  const candidateSource = useRef(new VectorSource());
  const existingSource = useRef(new VectorSource());
  const onToggleRef = useRef(onToggle);
  onToggleRef.current = onToggle;

  // The geometry set only; selection and names are applied in place below so
  // ticking a box never rebuilds the map or moves the camera.
  const geometryKey = candidates.map((c) => c.id).join(",");

  useEffect(() => {
    if (!containerRef.current) return;
    const candidateLayer = new VectorLayer({
      source: candidateSource.current,
      style: (f) => candidateStyle(f as Feature<Geometry>),
    });
    const map = new OpenLayersMap({
      target: containerRef.current,
      layers: [
        new TileLayer({
          source: new TileJSON({ url: lowResUrl, tileSize: 512, crossOrigin: "anonymous" }),
          maxZoom: 13,
        }),
        new TileLayer({ source: new XYZ({ url: highResUrl, maxZoom: 18 }), minZoom: 12 }),
        new VectorLayer({ source: existingSource.current, style: existingStyle }),
        candidateLayer,
      ],
      // A View without center/zoom renders nothing until fit() — keep a default.
      view: new View({ center: [0, 0], zoom: 2, maxZoom: 18 }),
      controls: [],
    });
    map.on("singleclick", (e) => {
      map.forEachFeatureAtPixel(
        e.pixel,
        (f) => {
          const id = f.get("candidateId");
          if (id && !f.get("disabled")) onToggleRef.current(String(id));
          return true;
        },
        { layerFilter: (l) => l === candidateLayer }
      );
    });
    map.on("pointermove", (e) => {
      const hit = map.hasFeatureAtPixel(e.pixel, { layerFilter: (l) => l === candidateLayer });
      map.getTargetElement().style.cursor = hit ? "pointer" : "";
    });
    mapRef.current = map;
    return () => {
      map.setTarget(undefined);
      mapRef.current = null;
    };
  }, []);

  useEffect(() => {
    existingSource.current.clear();
    existingSource.current.addFeatures(
      existing
        .map((f) => f.getGeometry()?.clone())
        .filter((g): g is Geometry => !!g)
        .map((g) => new Feature<Geometry>(g))
    );
  }, [existing]);

  useEffect(() => {
    const format = new GeoJSON();
    const source = candidateSource.current;
    source.clear();
    for (const c of candidates) {
      const f = format.readFeature(
        { type: "Feature", geometry: c.geometry, properties: {} },
        { dataProjection: "EPSG:4326", featureProjection: "EPSG:3857" }
      ) as Feature<Geometry>;
      f.setProperties({ candidateId: c.id, name: c.name, selected: c.selected, disabled: c.disabled });
      source.addFeature(f);
    }
    const extent = source.getExtent();
    if (mapRef.current && !isEmptyExtent(extent)) {
      mapRef.current.getView().fit(extent, { padding: [32, 32, 32, 32], maxZoom: 17 });
    }
    // Rebuilt only when the set of candidates changes; see geometryKey.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [geometryKey]);

  useEffect(() => {
    const byId = new Map(candidates.map((c) => [c.id, c] as const));
    for (const f of candidateSource.current.getFeatures()) {
      const c = byId.get(String(f.get("candidateId")));
      if (c) f.setProperties({ name: c.name, selected: c.selected, disabled: c.disabled });
    }
  }, [candidates]);

  return (
    <div
      ref={containerRef}
      style={{
        width: "100%",
        height,
        borderRadius: 8,
        overflow: "hidden",
        border: "1px solid #e5e7eb",
        flexShrink: 0,
      }}
    />
  );
};

export default FromTracksPreviewMap;
